// Late rehydration for evicted `toolResultStore` entries.
//
// Context: the chat's `toolResultStore` caches row-shaped tool
// results so `execute_python.from_tool_calls`, `expand_preview`, and
// `spawn_subagent.from_tool_calls` can pipe rows around without
// re-emitting them in tool-call args. The store is bounded — when
// large entries exceed the per-chat byte budget (or get dropped
// across a page-refresh / fork boundary), their rows go away but a
// marker stays.
//
// This module re-runs the original tool spec to repopulate a missing
// or evicted entry on demand. The spec lives in
// `agent.state.messages` — the assistant turn that emitted the
// toolCallId carries the input verbatim. We dispatch by tool name:
//   • analytics_query → re-execute the same spec under the same
//                       toolCallId. recordResult overwrites the
//                       marker with fresh rows.
//   • execute_python  → NOT rehydrated. The code's side effects on
//                       the Pyodide namespace can't be safely
//                       replayed across a refresh; the LLM should
//                       re-run setup if it needs Python state.
//   • everything else → not supported.
//
// All consumers (pythonTool, expand_preview, buildStagedInputs)
// share the same `resolveStoredOrRehydrate` helper so the
// "look-up-first, rehydrate-on-miss, then look-up-again" pattern is
// in one place.

import { getResult } from "./toolResultStore.js";

// Walk the agent's message array for the assistant turn that
// emitted `toolCallId`. Returns { name, args } or null.
function findToolCallInMessages(messages, toolCallId) {
    if (!Array.isArray(messages) || !toolCallId) return null;
    for (const m of messages) {
        if (m?.role !== "assistant") continue;
        const content = Array.isArray(m.content) ? m.content : [];
        for (const c of content) {
            if (c?.type === "toolCall" && c.id === toolCallId) {
                // pi-ai stores tool-call args under `arguments` in
                // state.messages; staged blocks use `input`. Reading only
                // `input` here passed `undefined` into the re-execution
                // below, silently breaking the eviction recovery this
                // module exists for. Fall back through both.
                return { name: c.name, args: c.arguments ?? c.input };
            }
        }
    }
    return null;
}

// Re-execute the original spec for a single toolCallId. Returns
//   { ok: true,  source }                — rehydration succeeded;
//                                          rows are now in the store
//                                          under `toolCallId`.
//   { ok: false, reason }                — couldn't rehydrate (spec
//                                          not found, unsupported
//                                          tool, execution failed).
//
// The tool's own `execute` is invoked directly (not through pi-ai's
// loop) so no agent events fire — the rehydration is invisible to
// the LLM's transcript. recordResult is called by the tool itself
// (analytics_query writes to the store on success), so a successful
// return means the namespace's entry has been refreshed.
export async function rehydrateToolResult({ namespaceKey, toolCallId, agentMessages, toolRegistry, signal }) {
    const found = findToolCallInMessages(agentMessages, toolCallId);
    if (!found) {
        // A harness step id (`<call>:s1`, `<call>:e2.1:1`) has no assistant
        // toolCall of its own; its rows live as long as the call that
        // produced them and cannot be re-executed from the transcript.
        const owner = String(toolCallId).split(/[.:]/)[0];
        if (owner !== toolCallId) {
            return { ok: false, reason: `${toolCallId} is a harness step of ${owner}; its rows were evicted and a step cannot be re-run from the transcript` };
        }
        return { ok: false, reason: `no assistant toolCall for id ${toolCallId} in current context` };
    }
    if (found.name !== "analytics_query") {
        return { ok: false, reason: `tool '${found.name}' does not support rehydration (only analytics_query)` };
    }
    const tool = (toolRegistry || []).find((t) => t.name === "analytics_query");
    if (!tool || typeof tool.execute !== "function") {
        return { ok: false, reason: "analytics_query tool not in registry" };
    }
    try {
        await tool.execute(toolCallId, found.args, signal);
    } catch (err) {
        return { ok: false, reason: err?.message || String(err) };
    }
    const stored = getResult(namespaceKey, toolCallId);
    if (stored && Array.isArray(stored.rows)) {
        return { ok: true, source: stored.source || "analytics_query" };
    }
    return { ok: false, reason: "rehydration completed but no rows landed in the store" };
}

// Bind a rehydrator to a specific agent/namespace/registry. Returned
// function takes just `(toolCallId, signal)` so callers don't have
// to thread context through. Lazy-resolves `agent` and `registry` so
// callers can build the rehydrator before the Agent is constructed
// (piChat.js does exactly this — chicken-and-egg with the tool
// registry).
export function createRehydrator({ getAgent, getNamespaceKey, getToolRegistry }) {
    return async function rehydrate(toolCallId, signal) {
        const agent = getAgent?.();
        const messages = agent?.state?.messages || [];
        const registry = getToolRegistry?.() || [];
        const namespaceKey = getNamespaceKey?.() || "default";
        return rehydrateToolResult({ namespaceKey, toolCallId, agentMessages: messages, toolRegistry: registry, signal });
    };
}

// Look up a stored entry; if missing or marked evicted, run
// rehydrate; look up once more. Returned shape:
//   { stored, rehydrated, reason }
//
//   stored      — the entry from `toolResultStore.getResult`, or null
//                 if neither the original lookup nor rehydration
//                 produced rows.
//   rehydrated  — true when we actually re-ran the spec.
//   reason      — diagnostic string when rehydration failed. The
//                 caller can fold this into its error message.
export async function resolveStoredOrRehydrate({ namespaceKey, toolCallId, rehydrate, signal }) {
    let stored = getResult(namespaceKey, toolCallId);
    if (stored && Array.isArray(stored.rows)) {
        return { stored, rehydrated: false };
    }
    if (typeof rehydrate !== "function") {
        return { stored: null, rehydrated: false, reason: stored?.evicted ? "evicted (no rehydrator wired)" : "not in store" };
    }
    const result = await rehydrate(toolCallId, signal);
    if (result?.ok) {
        stored = getResult(namespaceKey, toolCallId);
        if (stored && Array.isArray(stored.rows)) {
            return { stored, rehydrated: true };
        }
    }
    return { stored: null, rehydrated: false, reason: result?.reason || "rehydration failed" };
}
